import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { partnerTypeFieldGroups, platformPartnerTypeSlug, type PmmPartnerType } from '@eis/core';
import { PartnerFieldsForm } from '@eis/patterns';
import { roleLabel, type PartnerRefusal, type PartnerRoleRule } from '../../policies/model/partners';
import { PartnerSection } from '../../policies/ui/PartnerSection';
import { draftDisplayName, missingDraftFields, typeForSlug, type LeadPartnerState, type PartnerMode } from '../model/form';

/**
 * The lead's parties (SDD-013 §8.1.5, SDD-014 R-15): every pinned role filled through the PICKER —
 * the policy's own `PartnerSection`, searching the live Partner service — OR as "Neuer Partner": a
 * draft the accept will register (PS-5), typed from the live type catalog and filled in from that
 * type's own fields (PS-8), so the form is right for a type this build has never seen. One mode
 * per role, because the service refuses a role filled both ways. On a member the policyholder is
 * not offered (D7); the policy section says why.
 */
export function LeadPartnerSection({
  rules,
  state,
  onChange,
  refusal,
  draftRefusals,
  member,
  showDraftErrors,
  types,
  typesUnavailable,
  disabled,
}: {
  readonly rules: readonly PartnerRoleRule[];
  readonly state: LeadPartnerState;
  readonly onChange: (next: LeadPartnerState) => void;
  readonly refusal: PartnerRefusal;
  readonly draftRefusals: readonly string[];
  readonly member: boolean;
  readonly showDraftErrors: boolean;
  /** The Partner service's live types (platform spelling applied where compared). */
  readonly types: readonly PmmPartnerType[];
  /** The catalog could not be read: the draft falls back to a name and a type slug. */
  readonly typesUnavailable: boolean;
  readonly disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const idRules = rules.filter((rule) => state.mode[rule.roleErn ?? ''] !== 'draft');
  const draftRules = rules.filter((rule) => state.mode[rule.roleErn ?? ''] === 'draft');

  const setMode = (roleErn: string, mode: PartnerMode): void => {
    onChange({ ...state, mode: { ...state.mode, [roleErn]: mode } });
  };

  return (
    <Stack spacing={2}>
      {/* Per role: picker or new partner. The toggle is the one lead-specific control on the section. */}
      <Stack spacing={1} component="section" aria-label={t('leads.create.partnerMode.draft')}>
        {rules.map((rule) => {
          const roleErn = rule.roleErn ?? '';
          const mode = state.mode[roleErn] ?? 'id';
          return (
            <Stack key={roleErn} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ minWidth: 160 }}>
                {roleLabel(rule)}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                disabled={disabled === true}
                aria-label={`${roleLabel(rule)} · ${t('leads.create.partnerMode.draft')}`}
                onChange={(_event, next: PartnerMode | null) => {
                  if (next !== null) setMode(roleErn, next);
                }}
              >
                <ToggleButton value="id">{t('leads.create.partnerMode.id')}</ToggleButton>
                <ToggleButton value="draft">{t('leads.create.partnerMode.draft')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          );
        })}
      </Stack>

      {idRules.length > 0 && (
        <PartnerSection
          rules={idRules}
          values={state.ids}
          member={member}
          refusal={refusal}
          disabled={disabled}
          onChange={(roleErn, ids) => {
            onChange({ ...state, ids: { ...state.ids, [roleErn]: ids } });
          }}
        />
      )}

      {draftRules.length > 0 && (
        <Stack spacing={1.5} component="section" aria-label={t('leads.overview.drafts')}>
          <Typography variant="subtitle2" component="h3">
            {t('leads.overview.drafts')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('leads.create.draftHint')}
          </Typography>
          {typesUnavailable && <Alert severity="warning">{t('leads.create.draftTypesUnavailable')}</Alert>}
          {draftRefusals.map((message) => (
            <Alert key={message} severity="error" data-testid="draft-refusal">
              {message}
            </Alert>
          ))}
          {draftRules.map((rule) => {
            const roleErn = rule.roleErn ?? '';
            const draft = state.drafts[roleErn] ?? { typeCode: 'person', fields: {} };
            const required = (rule.minCount ?? 0) > 0;
            // The types the ROLE admits (P-10) narrow the live catalog; the catalog's names label them.
            const admitted = rule.partnerTypes !== undefined && rule.partnerTypes.length > 0 ? rule.partnerTypes : undefined;
            const options =
              types.length > 0
                ? [...new Map(types.map((type) => [platformPartnerTypeSlug(type.slug), type])).values()]
                    .filter((type) => admitted === undefined || admitted.includes(platformPartnerTypeSlug(type.slug)))
                    .map((type) => ({ value: platformPartnerTypeSlug(type.slug), label: type.name }))
                : (admitted ?? ['person', 'organisation']).map((slug) => ({
                    value: slug,
                    label: t(`leads.create.draftTypes.${slug}` as never, { defaultValue: slug }),
                  }));
            const type = typeForSlug(types, draft.typeCode);
            const groups = partnerTypeFieldGroups(type);
            const errors: Record<string, string> = {};
            if (showDraftErrors && required) {
              for (const key of missingDraftFields(draft, types)) errors[key] = t('policies.endorse.errors.required');
            }
            const nameMissing = showDraftErrors && required && type === undefined && draftDisplayName(draft.fields) === '';
            const setDraft = (patch: Partial<typeof draft>): void => {
              onChange({ ...state, drafts: { ...state.drafts, [roleErn]: { ...draft, ...patch } } });
            };
            return (
              <Stack key={roleErn} spacing={1.5} component="section" aria-label={`${roleLabel(rule)} · ${t('leads.create.partnerMode.draft')}`}>
                <TextField
                  size="small"
                  select
                  sx={{ maxWidth: 320 }}
                  disabled={disabled === true}
                  label={t('leads.create.draftType')}
                  value={draft.typeCode}
                  onChange={(event) => {
                    setDraft({ typeCode: event.target.value, fields: {} });
                  }}
                >
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                {type === undefined ? (
                  // No live type to build the form from: a name, in SDD-013's spelling, is what can be written.
                  <TextField
                    size="small"
                    fullWidth
                    required={required}
                    disabled={disabled === true}
                    label={`${roleLabel(rule)} · ${t('leads.create.draftName')}`}
                    value={draftDisplayName(draft.fields)}
                    error={nameMissing}
                    helperText={nameMissing ? t('policies.endorse.errors.required') : undefined}
                    onChange={(event) => {
                      const name = event.target.value;
                      const fields: Record<string, string> = {};
                      if (draft.typeCode === 'person') {
                        const at = name.lastIndexOf(' ');
                        fields['BasicInformation.firstName'] = at === -1 ? name : name.slice(0, at);
                        if (at !== -1) fields['BasicInformation.lastName'] = name.slice(at + 1);
                      } else {
                        fields['BasicInformation.organizationName'] = name;
                      }
                      setDraft({ fields });
                    }}
                  />
                ) : (
                  <PartnerFieldsForm
                    groups={groups}
                    values={draft.fields}
                    errors={errors}
                    disabled={disabled}
                    onChange={(key, value) => {
                      setDraft({ fields: { ...draft.fields, [key]: value } });
                    }}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
